import type { FC } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import ExitTicketIcon from '../../assets/exit-ticket-icon-xs.svg?react';
import Fire from '../../assets/Fire.svg?react';

interface Props {
  // Already worded by exitSlipChipLabel, e.g. "2 Exit tickets done" / "4 Exit tickets passed".
  label: string;
  // The same fire the skill's unit-path card shows, read off the same rule (isSkillOnFire) — which is
  // what keeps the two surfaces from disagreeing about a skill.
  isOnFire: boolean;
}

// The palette is the unit-path badge's, deliberately: DashboardCardExitSlipBadge carries these exact
// values, and a student walking from their roadmap into a skill should see the same marker.
const CHIP_BLUE = '#5776FC';
const CHIP_BLUE_EDGE = '#9FABFD';
const CHIP_FIRE = 'linear-gradient(180deg, #E33434 0%, #FF7C34 100%)';
const CHIP_FIRE_EDGE = '#F8946D';

/**
 * The exit-ticket chip in the skill page's header: how many of this skill's exit tickets the student
 * has got through, sitting beside "Your Result".
 *
 * A sibling of the unit-path card's badge rather than the same component. They share the rule and the
 * palette, not the shape: the card's badge is pinned over a card that navigates on click, so it has to
 * swallow every pointer event and open its own panel to stay reachable. This one is plain text in a
 * toolbar — the tooltip the design puts on this screen hangs off "Your Result", not off the chip, so
 * there is nothing here to interact with.
 *
 * The fire rides above the label inside the pill (the card has room above it; a toolbar row does not),
 * and keeps the card's looping bounce: it is the one thing on this header meant to pull the eye.
 */
export const SkillHeaderExitSlipChip: FC<Props> = ({ label, isOnFire }) => {
  const reduceMotion = useReducedMotion();

  return (
    <span
      style={{
        background: isOnFire ? CHIP_FIRE : CHIP_BLUE,
        borderColor: isOnFire ? CHIP_FIRE_EDGE : CHIP_BLUE_EDGE,
      }}
      // The understroke (a heavier bottom border, no other edge) is what gives the pill the slightly
      // raised look the rest of the exit-ticket chrome has. border-solid is not optional: Tailwind
      // leaves border-style at none, so widths alone would draw nothing.
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-solid tw-border-x-[1px] tw-border-t-[1px] tw-border-b-[2px] tw-px-2 tw-py-1 tw-shadow-md"
    >
      <ExitTicketIcon className="tw-h-3.5 tw-w-3.5 tw-rounded-[2px]" aria-hidden="true" />
      {isOnFire && (
        <motion.span
          animate={reduceMotion ? undefined : { y: [0, -2, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          className="tw-inline-flex"
        >
          <Fire className="tw-h-4 tw-w-4" aria-hidden="true" />
        </motion.span>
      )}
      <span className="tw-font-atkinson tw-text-sm tw-font-bold tw-leading-none tw-text-white">{label}</span>
    </span>
  );
};

export default SkillHeaderExitSlipChip;
